import { useEffect, useState } from "react";
import { usePwaMode } from "../hooks/usePwaMode";

const STORAGE_KEY = "money.install_hint_dismissed";

// First-visit-on-iOS-Safari card explaining how to install the PWA. iOS
// has no programmatic beforeinstallprompt event, so we surface the manual
// "tap Share → Add to Home Screen" path. Dismissed state persists in
// localStorage so the card doesn't reappear after the user has read it.
export function InstallHint() {
  const isPwa = usePwaMode();
  const [dismissed, setDismissed] = useState(true);
  const [isIosSafari, setIsIosSafari] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(STORAGE_KEY) === "1");
    setIsIosSafari(detectIosSafari());
  }, []);

  // Already installed → no need to suggest installing.
  if (isPwa) return null;
  // Other browsers (desktop Chrome/Edge, Android) get a native install
  // prompt from the browser, no card needed.
  if (!isIosSafari) return null;
  if (dismissed) return null;

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/70 p-4 flex items-start gap-3">
      <div className="text-2xl leading-none">📱</div>
      <div className="flex-1 text-sm">
        <div className="font-medium text-slate-100">Install Money</div>
        <p className="text-slate-400 mt-1">
          Tap the Share button in Safari, then{" "}
          <span className="text-slate-200">Add to Home Screen</span>. Opens
          chrome-less with a refresh and scroll-to-top button.
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          localStorage.setItem(STORAGE_KEY, "1");
          setDismissed(true);
        }}
        aria-label="Dismiss install hint"
        className="text-slate-500 hover:text-slate-300 text-lg leading-none px-1"
      >
        ×
      </button>
    </div>
  );
}

function detectIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as Mac; disambiguate by touch support.
    (ua.includes("Macintosh") && "ontouchend" in document);
  if (!isIos) return false;
  // Chrome/Firefox/Brave on iOS use WebKit but identify themselves in UA.
  // Their "Add to Home Screen" path is the same Safari one, so we don't
  // need to distinguish — but exclude the in-app webviews (Facebook,
  // Instagram) which can't install at all.
  const isInAppBrowser = /FBAN|FBAV|Instagram|Line\//.test(ua);
  return !isInAppBrowser;
}
